import React from 'react';

import { CircularProgress, Grid, Stack, Tab, Typography, useTheme } from '@mui/material';
import { useField } from 'formik';
import { IconProps, Trash } from 'iconsax-reactjs';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { removeCv } from '../../../../../../api/user';
import CvPreview from '../../../../../../components/CvPreview';
import LocaleIcon from '../../../../../../components/LanguageSwitcher/components/LocaleIcon';
import TabPanel from '../../../../../../components/TabPanel';
import { CV_FILE_SIZE, CV_SUPPORTED_FORMATS } from '../../../../../../constants/file';
import { IUser } from '../../../../../../types/user.type';
import {
  CvPreviewWrapperStyled,
  CvTabWrapperStyled,
  ErrorButtonStyled,
  TabsCvStyled,
} from '../../styles';
import { ICvType } from '../../types';
import CvForm from './components/CvForm';

interface IProps {
  user: IUser;
  isEditable: boolean;
  onDelete: (user: IUser) => void;
}

const CvTab = React.memo(({ user, isEditable, onDelete }: IProps) => {
  const [isDeleteProcess, setIsDeleteProcess] = React.useState<boolean>(false);
  const [version, setVersion] = React.useState<ICvType>(ICvType.en);
  const [field, , helpers] = useField('cv');
  const [fieldFr, , helpersFr] = useField('cv_fr');

  const theme = useTheme();

  const { t } = useTranslation();

  const editIconProps: IconProps = {
    size: 18,
    color: theme.palette.error[900],
  };

  const handleViewChange = React.useCallback((event: React.SyntheticEvent, newValue: ICvType) => {
    setVersion(newValue);
  }, []);

  const isValid = React.useCallback(
    (file: File) => {
      if (!CV_SUPPORTED_FORMATS.includes(file.type)) {
        toast.error(t('validation.file_format'));

        return false;
      }

      if (file.size > CV_FILE_SIZE) {
        toast.error(t('validation.file_size'));

        return false;
      }

      return true;
    },
    [t],
  );

  const handleDelete = React.useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();

      if (isDeleteProcess) {
        return;
      }

      setIsDeleteProcess(true);

      try {
        const response = await removeCv(version);

        onDelete(response);
      } catch {
        toast.error(t('profile.error_remove_cv'));
      } finally {
        setIsDeleteProcess(false);
      }
    },
    [isDeleteProcess, onDelete, t, version],
  );

  const handleDrop = React.useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();

      const file = event.dataTransfer.files[0];

      if (!isValid(file)) {
        return;
      }

      if (version === ICvType.fr) {
        helpersFr.setValue(file);

        return;
      }

      helpers.setValue(file);
    },
    [helpers, helpersFr, isValid, version],
  );

  const handleRemoveFile = React.useCallback(() => {
    if (version === ICvType.fr) {
      helpersFr.setValue(null);

      return;
    }

    helpers.setValue(null);
  }, [helpers, helpersFr, version]);

  const handleFileSelect = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = Array.from(event.target.files || []);

      const file = selectedFiles[0];

      event.target.value = '';

      if (!isValid(file)) {
        return;
      }

      if (version === ICvType.fr) {
        helpersFr.setValue(file);

        return;
      }

      helpers.setValue(file);
    },
    [helpers, helpersFr, isValid, version],
  );

  const isCvExist = React.useCallback(
    (type: ICvType = ICvType.en) => {
      if (!user) {
        return false;
      }

      if (type === 'fr') {
        return user.cv_fr && user.cv_fr_images;
      }

      return user.cv && user.cv_images;
    },
    [user],
  );

  const isCvFileExist = React.useCallback(
    (type: ICvType = ICvType.en) => {
      if (!user) {
        return false;
      }

      if (type === 'fr') {
        return user.cv_fr;
      }

      return user.cv;
    },
    [user],
  );

  return (
    <Grid container spacing={2}>
      <Stack direction="row" width="100%" justifyContent="space-between">
        <TabsCvStyled value={version} onChange={handleViewChange} aria-label="label tabs">
          <Tab
            value={ICvType.en}
            label={
              <Stack direction="row" alignItems="center" gap={1}>
                <LocaleIcon lang="en" />
                <Typography variant="md">{t('lang.en')}</Typography>
              </Stack>
            }
          />

          <Tab
            label={
              <Stack direction="row" alignItems="center" gap={1}>
                <LocaleIcon lang="fr" />
                <Typography variant="md">{t('lang.fr')}</Typography>
              </Stack>
            }
            value={ICvType.fr}
          />
        </TabsCvStyled>

        {isEditable && isCvFileExist(version) && (
          <ErrorButtonStyled onClick={handleDelete}>
            {isDeleteProcess ? (
              <CircularProgress size={editIconProps.size} color="neutral" />
            ) : (
              <Trash {...editIconProps} />
            )}
          </ErrorButtonStyled>
        )}
      </Stack>

      <Grid size={12}>
        <TabPanel key="manager_form_cv_en" value={version} index={ICvType.en}>
          <CvTabWrapperStyled>
            {isCvExist() && (
              <CvPreviewWrapperStyled>
                <CvPreview cv={user!.cv!} images={user!.cv_images!} />
              </CvPreviewWrapperStyled>
            )}

            {isEditable && (
              <CvForm
                handleDrop={handleDrop}
                handleFileSelect={handleFileSelect}
                handleRemoveFile={handleRemoveFile}
                file={field.value}
              />
            )}
          </CvTabWrapperStyled>
        </TabPanel>

        <TabPanel key="manager_form_cv_fr" value={version} index={ICvType.fr}>
          <CvTabWrapperStyled>
            {isCvExist(ICvType.fr) && (
              <CvPreviewWrapperStyled>
                <CvPreview cv={user!.cv_fr!} images={user!.cv_fr_images!} />
              </CvPreviewWrapperStyled>
            )}

            {isEditable && (
              <CvForm
                handleDrop={handleDrop}
                handleFileSelect={handleFileSelect}
                handleRemoveFile={handleRemoveFile}
                file={fieldFr.value}
              />
            )}
          </CvTabWrapperStyled>
        </TabPanel>
      </Grid>
    </Grid>
  );
});

export default CvTab;
